import { useQuery, useQueryCache } from '@pinia/colada';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { fetchToScheduleQueue, unmarkPoScheduled } from '@/services/purchaseOrderService';
import { PURCHASE_ORDERS_QUERY_KEY } from '@/store/purchaseOrderStore';
import type { MarkedPo, PurchaseOrder, ToScheduleQueue } from '@/types/purchaseOrders';
import { extractErrorMessage } from '@/utils/apiError';

export const QUEUE_HIDDEN_STORAGE_KEY = 'mpp.scheduling.queueHidden';
export const QUEUE_SHOW_MARKED_STORAGE_KEY = 'mpp.scheduling.queueShowMarked';
export const TO_SCHEDULE_SEGMENT = 'to-schedule';
// Matches the backend's snapshot window: asking more often would only re-read Postgres.
const QUEUE_STALE_MINUTES = 30;
const MS_PER_MINUTE = 60_000;
const EMPTY: ToScheduleQueue = { data: [], syncedAt: null, syncError: null, marked: [] };

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

export const usePoQueueStore = defineStore('poQueue', () => {
  const vendorId = ref<string | undefined>(undefined);
  const hidden = ref(readFlag(QUEUE_HIDDEN_STORAGE_KEY));
  const showMarked = ref(readFlag(QUEUE_SHOW_MARKED_STORAGE_KEY));
  // The vendor a forced refresh is currently in flight for — undefined once it settles. Scopes
  // the loading flag to whichever vendor is selected NOW, so a refresh left running after the
  // user switches vendors doesn't show as loading for the vendor they switched to.
  const refreshingFor = ref<string | undefined>(undefined);
  // Scoped the same way: a failed refresh for vendor A must not show in vendor B's footer.
  const refreshFailure = ref<{ vendorId: string; message: string } | null>(null);
  const queryCache = useQueryCache();

  const queryKey = (): [string, string, string] => [PURCHASE_ORDERS_QUERY_KEY, TO_SCHEDULE_SEGMENT, vendorId.value ?? ''];

  // Shares the purchase-orders prefix on purpose: capacityCalendarStore invalidates that prefix
  // after every write, which is exactly when remaining quantities change.
  const query = useQuery<ToScheduleQueue>({
    key: queryKey,
    query: () => fetchToScheduleQueue(vendorId.value as string, { refresh: false }),
    staleTime: QUEUE_STALE_MINUTES * MS_PER_MINUTE,
    refetchOnWindowFocus: true,
    enabled: computed(() => Boolean(vendorId.value)),
  });

  // pinia/colada keeps the previous data on a failed refetch (only `error` changes), so falling
  // back to EMPTY here would blank a list that already loaded successfully. `error` below is
  // the one place that decides whether there is "nothing to show".
  const current = computed<ToScheduleQueue>(() => query.data.value ?? EMPTY);
  const items = computed<PurchaseOrder[]>(() => current.value.data);
  const marked = computed<MarkedPo[]>(() => current.value.marked);
  const syncedAt = computed(() => current.value.syncedAt);
  const syncError = computed(() => current.value.syncError);
  const loading = computed(() => query.isLoading.value || refreshingFor.value === vendorId.value);
  // The full-column loading state — a background refetch keeps the list on screen instead.
  const initialLoading = computed(() => loading.value && items.value.length === 0);
  // Non-null only when there is nothing else to show; a background refetch failure must not
  // hide a list that already loaded.
  const error = computed(() => (items.value.length === 0 && query.error.value ? extractErrorMessage(query.error.value, 'Failed to load the queue') : null));
  const refreshError = computed(() => {
    const failure = refreshFailure.value;
    return failure && failure.vendorId === vendorId.value ? failure.message : null;
  });

  // A forced refresh bypasses the client cache and the backend's window in one call. The vendor
  // and cache key are captured up front so a vendor switch mid-flight can never write a stale
  // response into the wrong (or now-current) cache entry.
  async function refresh(): Promise<void> {
    const id = vendorId.value;
    if (!id) return;
    const key = queryKey();
    refreshFailure.value = null;
    refreshingFor.value = id;
    try {
      const result = await fetchToScheduleQueue(id, { refresh: true });
      if (vendorId.value !== id) return;
      queryCache.setQueryData(key, result);
    } catch (refreshCatch) {
      refreshFailure.value = { vendorId: id, message: extractErrorMessage(refreshCatch, 'Refresh failed') };
    } finally {
      if (refreshingFor.value === id) refreshingFor.value = undefined;
    }
  }

  function setHidden(value: boolean, options?: { persist?: boolean }): void {
    hidden.value = value;
    if (options?.persist === false) return;
    try {
      localStorage.setItem(QUEUE_HIDDEN_STORAGE_KEY, value ? 'true' : 'false');
    } catch {
      // The flag still applies for this session if storage is unavailable.
    }
  }

  function setShowMarked(value: boolean): void {
    showMarked.value = value;
    try {
      localStorage.setItem(QUEUE_SHOW_MARKED_STORAGE_KEY, value ? 'true' : 'false');
    } catch {
      // The flag still applies for this session if storage is unavailable.
    }
  }

  // Throws on failure so the caller can show the reason next to the list.
  async function unmark(poNumber: string): Promise<void> {
    const id = vendorId.value;
    if (!id) return;
    await unmarkPoScheduled(poNumber, id);
    await queryCache.invalidateQueries({ key: [PURCHASE_ORDERS_QUERY_KEY] });
  }

  return { vendorId, items, marked, syncedAt, syncError, loading, initialLoading, error, refreshError, refresh, hidden, setHidden, showMarked, setShowMarked, unmark };
});
